import {
  CartLineReferenceType,
  CartLineT,
  LineItemType,
} from '@lambda/apis/src/cart/types';
import { DeliveryEstimateT } from '@lambda/apis/src/catalog-v2/types';
import { ReactNode, useContext, useState } from 'react';
import classNames from 'classnames';
import { Zap } from 'lucide-react';
import { ShippingSKU } from '@lambda/apis/src/shipping/types';
import { AppCtx } from 'pages/_app';
import i18n from '@/settings/i18n';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import { generateFormattedDeliveryDates } from '@/components/v2/product/hooks/helpers';
import { priceFormatSuperscript } from '@/lib/price';
import { buildExpressShippingNotes } from '../helpers';
import { priceFormater } from '@/settings/index';

const t = i18n.init('cart');

type DeliveryOption = 'free' | 'express';

// Bolds the "free" word of the localized label: it's the all-caps word in
// every locale ("FREE Delivery", "Livraison GRATUITE").
const emphasizeFree = (label: string) => {
  const match = label.match(/\p{Lu}{2,}/u);

  if (!match || match.index == null) {
    return label;
  }

  return (
    <>
      {label.slice(0, match.index)}
      <span className="font-bold">{match[0]}</span>
      {label.slice(match.index + match[0].length)}
    </>
  );
};

/** Free delivery with nothing to choose between, as a single badge. */
export const FreeDeliveryBadge = ({
  date,
  className,
}: {
  /** Already formatted ("Oct 19 - 20"). */
  date?: string | null;
  className?: string;
}) => (
  <div
    className={classNames(
      'flex h-8 w-fit items-center rounded-full border border-[#EAEAEA] bg-[#F8F8F8] px-2.5 text-xs font-medium',
      className,
    )}
  >
    {/* A span, not loose text: as a direct flex child the space before
        "Delivery" would be dropped. */}
    <span className="whitespace-nowrap font-normal">
      {emphasizeFree(t`FREE Delivery`)}
      {date && ` ${date}`}
    </span>
  </div>
);

const DeliveryToggle = ({
  line,
  className,
  expressShippingOffer,
  deliveryEstimates,
  pills = false,
}: {
  line: CartLineT;
  className?: string;
  expressShippingOffer?: ShippingSKU;
  deliveryEstimates?: DeliveryEstimateT[];
  /** The purchase flow's: each option its own pill, wrapping only without room. */
  pills?: boolean;
}) => {
  const { getExpressShippingForProduct, addToCart, removeFromCart } =
    useCartContext();
  const [isUpdating, setIsUpdating] = useState(false);
  const { zipcodeStateData } = useContext(AppCtx);

  // Same estimate and format as the PDP's delivery strip, so the dates read
  // the same on both ("Oct 19 - 20").
  const {
    formattedStandardDelivery: freeDeliveryDate,
    formattedExpressDelivery: expressDeliveryDate,
  } = generateFormattedDeliveryDates({
    deliveryEstimates,
    format: 'short',
    destinationState: zipcodeStateData?.state,
    destinationRegion: zipcodeStateData?.region,
  });

  // Selection reflects the cart: express is "on" when its shipping line exists.
  const expressShippingLine = getExpressShippingForProduct(line.id);
  const selected: DeliveryOption = expressShippingLine ? 'express' : 'free';

  // Without an express option there's nothing to choose between — show a single
  // FREE delivery badge instead of a radio group.
  if (expressShippingOffer == null) {
    return <FreeDeliveryBadge date={freeDeliveryDate} className={className} />;
  }

  const onSelect = async (option: DeliveryOption) => {
    if (option === selected || isUpdating) {
      return;
    }

    setIsUpdating(true);

    try {
      if (option === 'express') {
        await addToCart({
          typeId: expressShippingOffer.id,
          quantity: line.quantity,
          notes: buildExpressShippingNotes(line),
          type: LineItemType.SHIPPING,
          reference: {
            type: CartLineReferenceType.CART_LINE,
            typeId: line.id,
          },
        });
      } else if (expressShippingLine) {
        await removeFromCart(expressShippingLine, line);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const options: { value: DeliveryOption; content: ReactNode }[] = [
    {
      value: 'free',
      content: (
        <span className={classNames(!pills && 'whitespace-nowrap')}>
          {emphasizeFree(t`FREE Delivery`)}
          {freeDeliveryDate && ` ${freeDeliveryDate}`}
        </span>
      ),
    },
    {
      value: 'express',
      content: (
        // A pill may be narrower than the line on small phones: it wraps
        // there, a word group at a time, instead of overflowing the card.
        <span
          className={classNames(
            'flex items-center gap-x-1',
            pills ? 'flex-wrap' : 'whitespace-nowrap',
          )}
        >
          <Zap className="h-3.5 w-3.5 shrink-0" />
          <span className="font-bold">{t`Express`}</span>
          {expressDeliveryDate && <span>{expressDeliveryDate}</span>}
          <span translate="no">
            (+
            {priceFormatSuperscript(
              priceFormater.long(expressShippingOffer.price / 100),
            )}
            )
          </span>
        </span>
      ),
    },
  ];

  return (
    <div
      className={classNames(
        pills
          ? 'flex flex-wrap items-center gap-2'
          : 'flex flex-col gap-x-2 overflow-hidden rounded-xl border bg-white md:flex-row md:border-0 lg:flex-col lg:border xl:flex-row xl:items-center xl:border-0',
        className,
      )}
      role="radiogroup"
    >
      {options.map((option) => {
        const isSelected = selected === option.value;

        return (
          <button
            type="button"
            key={option.value}
            role="radio"
            aria-checked={isSelected}
            disabled={isUpdating}
            onClick={() => onSelect(option.value)}
            className={classNames(
              'group flex items-center gap-x-1.5 text-xs font-normal transition-colors focus:outline-none disabled:opacity-60',
              pills
                ? [
                    'min-h-8 w-fit rounded-full border bg-white py-1 pl-2 pr-2.5 text-left',
                    isSelected
                      ? 'border-gray-700'
                      : 'border-[#E6E7EB] hover:border-gray-300',
                  ]
                : [
                    'h-10 w-full rounded-none border-0 px-2.5 md:h-8 md:w-fit md:rounded-full md:border lg:h-10 lg:w-full lg:rounded-none lg:border-0 xl:h-8 xl:w-fit xl:rounded-full xl:border',
                    // The border only shows as pills (md, xl); stacked, the
                    // selected row is marked by its fill.
                    isSelected
                      ? 'border-gray-700 bg-[#F8F8F8]'
                      : 'border-[#EAEAEA] bg-white hover:border-gray-300',
                  ],
            )}
          >
            <span
              className={classNames(
                'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border transition-colors',
                {
                  'border-gray-700': isSelected,
                  'group-hover:border-gray-600 border-gray-400': !isSelected,
                },
              )}
            >
              <span
                className={classNames(
                  'h-2 w-2 rounded-full transition-opacity',
                  {
                    'bg-gray-700 opacity-100': isSelected,
                    'bg-gray-700/60 opacity-0 group-hover:opacity-100':
                      !isSelected,
                  },
                )}
              />
            </span>
            {option.content}
          </button>
        );
      })}
    </div>
  );
};

export default DeliveryToggle;
